import queryString from 'query-string';
import sentryFetch from '@amzn/sentry-fetch';

function enhancedFetch(url: string, options: RequestInit = {}): Promise<Response> {
    return sentryFetch(url, {
        ...options,
        sentryOptions: {
            followMidwayStepUp: true,
        }
    });
}

function fetchWithRetry(url: string, options: any = {}, maxRetries = 0): Promise<any> {
    const { query = null, ...init } = options;
    init.credentials = 'include';

    const queryStr = query ? `?${typeof query === 'string' ? query : queryString.stringify(query)}` : '';

    async function attempt(retryCount: number): Promise<any> {
        try {
            const response = await enhancedFetch(`${url}${queryStr}`, init);
            if (response.status < 400) {
                return parseResponse(response);
            }

            const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
            (error as any).httpStatus = response.status;

            if (retryCount < maxRetries && response.status >= 500) {
                await new Promise(resolve => setTimeout(resolve, 1000));
                return attempt(retryCount + 1);
            }
            throw error;
        } catch (error: any) {
            if (retryCount < maxRetries && (!error.httpStatus || error.httpStatus >= 500)) {
                await new Promise(resolve => setTimeout(resolve, 1000));
                return attempt(retryCount + 1);
            }
            throw error;
        }
    }
    return attempt(0);
}

async function parseResponse(response: Response): Promise<any> {
    const text = await response.text();
    try {
        const parsed = JSON.parse(text);
        // Handle Lambda proxy integration response format
        if (parsed.statusCode && parsed.body) {
            return JSON.parse(parsed.body);
        }
        return parsed;
    } catch {
        return text;
    }
}

export const getApiUrlForRealm = (stage: string, realm: string): string => {
    // Local dev: point the UI at the local bb server (LocalDevServer on :8080).
    // Gated on NODE_ENV==='development' (same pattern as AuthClient) so unit tests,
    // which run under NODE_ENV='test' in jsdom (hostname 'localhost'), keep the real URL.
    if (process.env.NODE_ENV === 'development'
            && typeof window !== 'undefined' && window.location.hostname === 'localhost') {
        return 'http://localhost:8080';
    }
    return `https://api-${stage.toLowerCase()}-${realm.toLowerCase()}.sherlock.payments.amazon.dev`;
};

export const makeAwsSignedRequest = (stage: string, realm: string, endpoint: string, options: any = {}): Promise<any> => {
    const baseUrl = getApiUrlForRealm(stage, realm);
    const url = `${baseUrl}/${endpoint.replace(/^\//, '')}`;
    const maxRetries = options.retry !== false ? 2 : 0;

    return fetchWithRetry(url, options, maxRetries);
};
